import { Log, OidcClient, OidcClientSettings, SigninRequest, User, UserManager, UserManagerSettings } from "oidc-client-ts";

Log.setLogger(console);
Log.setLevel(Log.INFO);

// singleton class
export default class AuthenticationService {

  public static getInstance(): AuthenticationService {
    return AuthenticationService.instance;
  }
  private static instance: AuthenticationService = new AuthenticationService();

  public userManager: UserManager;
  public oidcClient: OidcClient;
  private user: User = null;

  // Generic prefix sweep for session-scoped UI state that must not survive a login or a logout.
  // Feature modules opt in by giving their sessionStorage keys one of these prefixes — no import of
  // the feature module needed here. "ai-thread-" is AI Chat's; "npm_sort_group_" is the Project
  // Argus Dashboard's Sort By / Group By persistence (see network-portfolio-management/sortGroupStorage.ts).
  // Swept identically on both boundaries: clearing on logout is defense in depth, and clearing on
  // login is the actual guarantee this login-restart behavior depends on.
  private static readonly SESSION_SCOPED_KEY_PREFIXES = ["ai-thread-", "npm_sort_group_"];

  private constructor() {
    const settings: UserManagerSettings = {
      authority: window.authEndpoint,
      automaticSilentRenew: true,
      client_id: "omnius.js",
      monitorSession: true,
      post_logout_redirect_uri: window.customUIAbsolutePath,
      redirect_uri: `${window.customUIAbsolutePath}/callback`,
      response_type: "code",
      revokeTokensOnSignout: true,
      scope: "openid profile email role api1 IdentityServerApi QuestCustom qesi",
      silent_redirect_uri: `${window.customUIAbsolutePath}/silent-refresh-callback`,
    };
    this.userManager = new UserManager(settings);
    this.oidcClient = new OidcClient(settings as OidcClientSettings);

    this.userManager.events.addAccessTokenExpired(async () => {
      const token = this.user.id_token;

      await this.removeUser();
      await this.userManager.signoutRedirect({ id_token_hint: token });
    });

    this.userManager.events.addUserSignedOut(async () => {
      await this.removeUser();
      await this.login();
    });
  }

  public getUser(): User | null {
    return this.user
      ? { ...this.user } as User
      : null;
  }

  public async loadUser(): Promise<User | null> {
    this.user = await this.userManager.getUser();
    return this.getUser();
  }

  public login(): Promise<void> {
    // save the current path in session so we can restore the path in authenticationCallback.tsx
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("LoginRedirect", currentPath);
    return this.userManager.signinRedirect();
  }

  public async renewToken(): Promise<User> {
    this.user = await this.userManager.signinSilent();
    return this.getUser();
  }

  public async logout(logoutReason: string): Promise<void> {
    const user = this.user;
    await this.removeUser();
    return this.userManager.signoutRedirect({
      extraQueryParams: {
        logoutReason,
      },
      id_token_hint: user ? user.id_token : null,
    });
  }

  public async removeUser(): Promise<void> {
    // if revokeTokens rejects for some reason, we don't want it to stop the rest of the function or caller
    try {
      await this.userManager.revokeTokens();
    } catch {
    } finally {
      this.user = null;
      this.clearSessionScopedUiState();
      await this.userManager.removeUser();
      await this.userManager.clearStaleState();
    }
  }

  public async signInCallback(): Promise<User | null> {
    // if signinCallback returns undefined, keep the current user
    const newUser = await this.userManager.signinCallback();
    this.user = newUser || this.user || null;
    return this.getUser();
  }

  public async signInRedirectCallback(): Promise<User | null> {
    this.user = await this.userManager.signinRedirectCallback();
    // Every completed interactive login funnels through here (silent renewal uses
    // signInSilentCallback() instead, via a separate hidden-iframe flow — never hits this method).
    // Clear unconditionally on success so a fresh login never inherits a stale session's selection,
    // regardless of how the previous session ended.
    this.clearSessionScopedUiState();
    return this.getUser();
  }

  public createSignInRequest(): Promise<SigninRequest> {
    return this.oidcClient.createSigninRequest({});
  }

  public isUserSignedIn(): boolean {
    return this.user && this.user.access_token && !this.user.expired;
  }

  private clearSessionScopedUiState(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && AuthenticationService.SESSION_SCOPED_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }
}
